import { Briefcase, ShieldCheck, DollarSign, Bell, Check, X, Ban, RefreshCw, Edit2, Trash2, TrendingUp, Clock, Flag, CheckCircle2, Wallet, ExternalLink } from "lucide-react";
import type { AdminTalent } from "../../data";
import { useAdminData } from "../../hooks/useAdminData";
import { talents as talentsApi } from "../../api/endpoints";
import { ConfirmState } from "../../components/feedback/ConfirmModal";
import { AddToast } from "../../components/feedback/Toast";
import { Avatar } from "../../components/ui/Avatar";
import { Btn } from "../../components/ui/Btn";
import { Card } from "../../components/ui/Card";
import { KpiCard } from "../../components/ui/KpiCard";
import { SectionLabel } from "../../components/ui/SectionLabel";
import { StatusBadge } from "../../components/ui/StatusBadge";
import { openInApp, sendNotificationTo } from "../shared/detailActions";
import { NotesCard, focusNotes } from "../shared/NotesCard";
import { F, T } from "../../theme/tokens";
import { BackLink } from "../../components/ui/BackLink";
import { DetailActionBar } from "../../components/ui/DetailActionBar";

// ── Talent detail ────────────────────────────────────────────────────────────

export function TalentDetailView({ talent, onBack, addToast, confirm }: { talent: AdminTalent; onBack: () => void; addToast: AddToast; confirm: (opts: Omit<ConfirmState, "open">) => void }) {
  const { ADMIN_APPLICATIONS, ADMIN_PAYMENTS, refetchTalents } = useAdminData();
  const relatedApps = ADMIN_APPLICATIONS.filter((a) => a.talentId === talent.id);
  const relatedPayments = ADMIN_PAYMENTS.filter((p) => p.talentId === talent.id);

  function setVerification(status: "Verified" | "Rejected") {
    talentsApi.update(talent.id, { verification_status: status })
      .then(() => { refetchTalents(); addToast(status === "Verified" ? "Talent verified" : "Verification rejected", status === "Verified" ? "success" : "error"); })
      .catch(() => addToast("Failed to update verification", "error"));
  }
  function setAccountStatus(status: "Active" | "Suspended") {
    talentsApi.update(talent.id, { account_status: status })
      .then(() => { refetchTalents(); addToast(`${talent.name} ${status === "Suspended" ? "suspended" : "reactivated"}`, status === "Suspended" ? "error" : "success"); })
      .catch(() => addToast("Failed to update account status", "error"));
  }
  // A suspended or archived account cannot be reached or reviewed, so those actions go away.
  const closed = talent.account !== "Active";
  const archived = talent.account === "Archived";
  const canRevoke = talent.verification === "Verified" && !closed;
  function deleteTalent() {
    talentsApi.remove(talent.id)
      .then(() => { refetchTalents(); addToast(`${talent.name} archived`, "error"); onBack(); })
      .catch(() => addToast("Failed to delete talent", "error"));
  }

  return (
    <div>
      <BackLink onClick={onBack} label="Back to Talent" />

      <div className="detail-head" style={{ display: "flex", alignItems: "flex-start", gap: "20px", marginBottom: "28px", flexWrap: "wrap" }}>
        <Avatar className="detail-avatar" initials={talent.initials} color={talent.color} size={64} />
        <div className="detail-info" style={{ flex: 1 }}>
          <div className="detail-title" style={{ display: "flex", alignItems: "center", gap: "10px", flexWrap: "wrap" }}>
            <h1 style={{ fontSize: "20px", fontWeight: 700, color: T.text, fontFamily: F, letterSpacing: "-0.02em", margin: 0 }}>{talent.name}</h1>
            <StatusBadge status={talent.verification} />
            <StatusBadge status={talent.account} />
            {talent.flagged && <span style={{ display: "flex", alignItems: "center", gap: "4px", fontSize: "11px", color: "#991b1b", fontFamily: F, fontWeight: 600 }}><Flag size={11} /> Flagged</span>}
          </div>
          <p style={{ fontSize: "13px", color: T.sub, fontFamily: F, marginTop: "4px" }}>{talent.category} · {talent.location}</p>
          <p style={{ fontSize: "12px", color: T.muted, fontFamily: F, marginTop: "2px" }}>{talent.email} · {talent.phone}</p>
        </div>
        <div className="detail-actions" style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
          <Btn variant="ghost" onClick={() => openInApp(`/talents/${talent.id}`, addToast)}><ExternalLink size={13} /> View profile</Btn>
          {talent.verification === "Pending" && (
            <>
              <Btn variant="primary" onClick={() => setVerification("Verified")}><Check size={13} /> Approve</Btn>
              <Btn variant="destructive" onClick={() => setVerification("Rejected")}><X size={13} /> Reject</Btn>
            </>
          )}
          {talent.account === "Active" && (
            <Btn variant="ghost" onClick={() => confirm({ title: `Suspend ${talent.name}?`, body: "This will prevent the talent from applying or receiving messages.", destructive: true, confirmLabel: "Suspend", onConfirm: () => setAccountStatus("Suspended") })}>
              <Ban size={13} /> Suspend
            </Btn>
          )}
          {closed && (
            <Btn variant="ghost" onClick={() => setAccountStatus("Active")}><RefreshCw size={13} /> {archived ? "Restore account" : "Reactivate"}</Btn>
          )}
        </div>
      </div>

      {/* Stats + balances */}
      <div className="rg-6" style={{ display: "grid", gridTemplateColumns: "repeat(6,1fr)", gap: "12px", marginBottom: "22px" }}>
        <KpiCard icon={<Briefcase size={15} />} label="Applications" value={talent.totalApplications.toString()} />
        <KpiCard icon={<CheckCircle2 size={15} />} label="Hires" value={talent.totalHires.toString()} />
        <KpiCard icon={<DollarSign size={15} />} label="Total earnings" value={talent.totalEarnings} />
        <KpiCard icon={<Wallet size={15} />} label="Wallet balance" value={talent.walletBalance} />
        <KpiCard icon={<Clock size={15} />} label="Pending payout" value={talent.pendingPayout} />
        <KpiCard icon={<TrendingUp size={15} />} label="Rating" value={talent.rating > 0 ? `${talent.rating} / 5` : "—"} sub={talent.reviews > 0 ? `${talent.reviews} reviews` : "No reviews"} />
      </div>

      <div className="rg-split" style={{ display: "grid", gridTemplateColumns: "2fr 1fr", gap: "14px" }}>
        <div style={{ display: "flex", flexDirection: "column", gap: "14px" }}>
          <Card style={{ padding: "20px" }}>
            <SectionLabel>Profile</SectionLabel>
            <p style={{ fontSize: "13px", color: T.sub, fontFamily: F, lineHeight: 1.7 }}>{talent.bio}</p>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "12px", marginTop: "16px" }}>
              {[["Day rate", talent.dayRate], ["Joined", talent.joinedDate], ["Last active", talent.lastActive]].map(([l, v]) => (
                <div key={l}>
                  <p style={{ fontSize: "10px", color: T.muted, fontFamily: F, textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: "2px" }}>{l}</p>
                  <p style={{ fontSize: "12px", fontWeight: 500, color: T.text, fontFamily: F }}>{v}</p>
                </div>
              ))}
            </div>
          </Card>

          <Card style={{ padding: "20px" }}>
            <SectionLabel>Applications</SectionLabel>
            {relatedApps.length === 0 ? <p style={{ fontSize: "12px", color: T.muted, fontFamily: F }}>No applications yet.</p> : (
              relatedApps.map((a, i) => (
                <div key={a.id} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "9px 0", borderBottom: i < relatedApps.length - 1 ? `1px solid ${T.border}` : "none" }}>
                  <div>
                    <p style={{ fontSize: "12px", fontWeight: 500, color: T.text, fontFamily: F }}>{a.postingTitle}</p>
                    <p style={{ fontSize: "10px", color: T.sub, fontFamily: F }}>{a.orgName} · {a.appliedDate}</p>
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                    <p style={{ fontSize: "11px", color: T.sub, fontFamily: F }}>{a.proposedRate}</p>
                    <StatusBadge status={a.status} />
                  </div>
                </div>
              ))
            )}
          </Card>
        </div>

        <div style={{ display: "flex", flexDirection: "column", gap: "14px" }}>
          <Card style={{ padding: "20px" }}>
            <SectionLabel>Payments</SectionLabel>
            {relatedPayments.length === 0 ? <p style={{ fontSize: "12px", color: T.muted, fontFamily: F }}>No payments yet.</p> : (
              relatedPayments.map((p, i) => (
                <div key={p.id} style={{ padding: "9px 0", borderBottom: i < relatedPayments.length - 1 ? `1px solid ${T.border}` : "none" }}>
                  <div style={{ display: "flex", justifyContent: "space-between" }}>
                    <p style={{ fontSize: "11px", fontWeight: 600, color: T.text, fontFamily: F }}>{p.netToTalent}</p>
                    <StatusBadge status={p.status} />
                  </div>
                  <p style={{ fontSize: "10px", color: T.sub, fontFamily: F, marginTop: "2px" }}>{p.postingTitle}</p>
                  <p style={{ fontSize: "9px", color: T.muted, fontFamily: "'Space Mono', monospace", marginTop: "2px" }}>{p.date} · {p.txnRef}</p>
                </div>
              ))
            )}
          </Card>

          <Card className="hide-phone" style={{ padding: "20px" }}>
            <SectionLabel>Admin actions</SectionLabel>
            <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
              {/* Revoking verification makes them resubmit their documents in
                  the app — which is what "re-verify" can honestly mean while
                  verification is self-serve and instant there. */}
              {canRevoke && <Btn variant="ghost" onClick={() => setVerification("Rejected")} style={{ justifyContent: "flex-start", width: "100%" }}><ShieldCheck size={12} /> Revoke verification</Btn>}
              {!closed && <Btn variant="ghost" onClick={() => sendNotificationTo("talent", talent.id, talent.name, addToast)} style={{ justifyContent: "flex-start", width: "100%" }}><Bell size={12} /> Send notification</Btn>}
              <Btn variant="ghost" onClick={focusNotes} style={{ justifyContent: "flex-start", width: "100%" }}><Edit2 size={12} /> Add internal note</Btn>
              {!archived && (
                <Btn variant="destructive" onClick={() => confirm({ title: `Archive ${talent.name}?`, body: "They can no longer sign in. Applications, engagements and payment history are kept — nothing on this platform is ever hard-deleted.", destructive: true, confirmLabel: "Archive account", onConfirm: deleteTalent })} style={{ justifyContent: "flex-start", width: "100%" }}>
                  <Trash2 size={12} /> Archive account
                </Btn>
              )}
            </div>
          </Card>

          <NotesCard entityType="Talent" entityId={talent.id} addToast={addToast} />
        </div>
      </div>

      <DetailActionBar
        onDelete={archived ? undefined : () => confirm({ title: `Archive ${talent.name}?`, body: "They can no longer sign in. Applications, engagements and payment history are kept — nothing on this platform is ever hard-deleted.", destructive: true, confirmLabel: "Archive account", onConfirm: deleteTalent })}
        items={[
          ...(canRevoke ? [{ label: "Revoke verification", icon: <ShieldCheck size={15} />, onClick: () => setVerification("Rejected") }] : []),
          ...(!closed ? [{ label: "Send notification", icon: <Bell size={15} />, onClick: () => sendNotificationTo("talent", talent.id, talent.name, addToast) }] : []),
          { label: "Add internal note", icon: <Edit2 size={15} />, onClick: focusNotes },
        ]}
      />
    </div>
  );
}
